import type { HttpErrorResponse } from '@angular/common/http';

import type { ActionScopedPayload, PaginatedActionScopedPayload, PaginationParams } from '../types';
import { pipeState } from '../utils';
import type {
  EntityCollectionAdapter,
  EntityPartial,
  EntityState,
  GenericEntity,
  GenericIdSelector,
  GenericMetaOptions,
  ID,
  Scope,
} from './types';
import { resolveScope } from './utils';

export type EntityUpdateOperationPayload<Entity extends GenericEntity> =
  EntityOperationAsyncPayload<EntityPartial<Entity>>;

export type EntityOperationSyncPayload<Entity> = {
  data: Entity;
};

export type EntityOperationAsyncPayload<Entity> = {
  data: Entity;
  correlationId: string;
};

type InferIdOnlyType<IdSelector> = IdSelector extends GenericIdSelector<infer R> ? R : never;
type PartialEntity<
  IdSelector extends GenericIdSelector<any>,
  Entity,
> = InferIdOnlyType<IdSelector> & Partial<Entity>;

export type CreateOneOptimisticPayload<T extends GenericEntity> = EntityOperationAsyncPayload<T> &
  ActionScopedPayload<T>;

/* eslint-disable max-lines */
// eslint-disable-next-line max-lines-per-function
export const getEntityCollectionActions = <
  Entity extends GenericEntity,
  IdSelector extends GenericIdSelector<Entity>,
  MetaOptions extends GenericMetaOptions,
>(
  adapter: EntityCollectionAdapter<Entity, MetaOptions>,
  selectId: IdSelector,
) => ({
  fetchOne: (
    state: EntityState<Entity, MetaOptions>,
    { id }: { id: ID },
  ): EntityState<Entity, MetaOptions> =>
    adapter.markAsPending(state, { scope: null, entities: [id] }),
  fetchOneSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data }: { data: Entity },
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next => adapter.writeOne(next, data),
      next =>
        adapter.markAsLoaded(next, {
          entities: [selectId(data)],
          scope: null,
        }),
    ),
  fetchOneFailed: (
    state: EntityState<Entity, MetaOptions>,
    { id, error }: { id: ID; error: HttpErrorResponse },
  ): EntityState<Entity, MetaOptions> =>
    adapter.setError(state, error, {
      scope: null,
      entities: [id],
    }),

  fetchCollection: (
    state: EntityState<Entity, MetaOptions>,
    payload?: { scope: Scope },
  ): EntityState<Entity, MetaOptions> =>
    adapter.markAsPending(state, { scope: resolveScope(payload?.scope), entities: [] }),
  fetchCollectionSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data, scope }: ActionScopedPayload<Entity[]>,
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next => adapter.setMany(next, data, { scope }),
      next =>
        adapter.markAsLoaded(next, {
          scope: resolveScope(scope),
          entities: data.map(e => selectId(e)),
        }),
    ),
  fetchCollectionFailed: (
    state: EntityState<Entity, MetaOptions>,
    payload: { error: HttpErrorResponse; scope?: Scope },
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next => adapter.markAsLoaded(next, { scope: resolveScope(payload.scope), entities: [] }),
      next =>
        adapter.setError(next, payload.error, {
          scope: resolveScope(payload.scope),
          entities: [],
        }),
    ),

  fetchPaginatedCollection: <U extends PaginationParams>(
    state: EntityState<Entity, MetaOptions>,
    payload: { scope: Scope; params: U; add?: boolean },
  ): EntityState<Entity, MetaOptions> =>
    adapter.markAsPending(state, { scope: resolveScope(payload?.scope), entities: [] }),
  fetchPaginatedCollectionSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data, scope, add }: PaginatedActionScopedPayload<Entity[]>,
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next =>
        add
          ? adapter.addMany(next, data.rows, { scope })
          : adapter.setMany(next, data.rows, { scope }),
      next => adapter.setPaginationMeta(next, data.meta, { scope }),
      next =>
        adapter.markAsLoaded(next, {
          scope,
          entities: data.rows.map(e => e.id),
        }),
    ),
  fetchPaginatedCollectionFailed: (
    state: EntityState<Entity, MetaOptions>,
    payload: { error: HttpErrorResponse; scope?: Scope },
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next => adapter.markAsLoaded(next, { scope: resolveScope(payload.scope), entities: [] }),
      next =>
        adapter.setError(next, payload.error, {
          scope: resolveScope(payload.scope),
          entities: [],
        }),
    ),

  createOne: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationSyncPayload<Entity> & ActionScopedPayload<Entity>,
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next =>
        adapter.markAsLoaded(next, {
          entities: [selectId(payload.data)],
          scope: null,
        }),
      next =>
        adapter.addOne(next, payload.data, {
          scope: payload.scope,
        }),
    ),

  createOneOptimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Partial<Entity>> & ActionScopedPayload<Partial<Entity>>,
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next =>
        adapter.markAsPending(next, {
          entities: [payload.correlationId],
          scope: null,
        }),
      next =>
        adapter.addOne(next, <Entity>payload.data, {
          scope: payload.scope,
          correlationId: payload.correlationId,
        }),
    ),
  createOneOptimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Entity> & ActionScopedPayload<Entity>,
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next => adapter.replaceOne(next, payload.correlationId, payload.data),
      next =>
        adapter.markAsLoaded(next, {
          entities: [selectId(payload.data)],
          scope: null,
        }),
      next =>
        adapter.setAlias(next, {
          [payload.correlationId]: selectId(payload.data),
        }),
    ),
  createOneOptimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Entity> &
      ActionScopedPayload<Entity> & { error: HttpErrorResponse },
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next => adapter.removeOne(next, payload.correlationId),
      next =>
        adapter.setError(next, payload.error, { scope: null, entities: [payload.correlationId] }),
    ),

  createOnePessimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload:
      | (EntityOperationAsyncPayload<Entity> & ActionScopedPayload<Entity>)
      | (EntityOperationAsyncPayload<Partial<Entity>> & ActionScopedPayload<Partial<Entity>>),
  ): EntityState<Entity, MetaOptions> => {
    const id = payload.correlationId || selectId(<Entity>payload.data);

    if (!id) {
      throw new Error('key of entity could not be determined');
    }

    return adapter.markAsPending(state, {
      entities: [id],
      scope: null,
    });
  },
  createOnePessimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Entity> & ActionScopedPayload<Entity>,
  ): EntityState<Entity, MetaOptions> => {
    const id = selectId(payload.data);

    return pipeState(
      state,
      next =>
        adapter.markAsLoaded(next, {
          entities: [id],
          scope: null,
        }),
      next =>
        payload.correlationId
          ? pipeState(
              next,
              next => adapter.removeOne(next, payload.correlationId),
              next =>
                adapter.setAlias(next, {
                  [payload.correlationId]: id,
                }),
            )
          : next,
      next => adapter.addOne(next, { ...payload.data }, { scope: payload.scope }),
    );
  },
  createOnePessimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    payload: { error: HttpErrorResponse; correlationId: string; scope?: Scope },
  ): EntityState<Entity, MetaOptions> =>
    adapter.setError(state, payload.error, { entities: [payload.correlationId], scope: null }),

  updateOne: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationSyncPayload<Entity>,
  ): EntityState<Entity, MetaOptions> => adapter.updateOne(state, payload.data),

  updateOneOptimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Entity> & ActionScopedPayload<Entity>,
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next =>
        adapter.optimisticUpdateSet(next, payload.correlationId, {
          before: state.entityMap[selectId(payload.data)]!,
          update: payload.data,
        }),
      next => adapter.updateOne(next, payload.data),
      next =>
        adapter.markAsPending(next, {
          entities: [selectId(payload.data)],
          scope: payload.scope ?? null,
        }),
    ),
  updateOneOptimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    {
      data,
      correlationId,
      scope,
    }: ActionScopedPayload<Entity> & EntityUpdateOperationPayload<Entity>,
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next => adapter.markAsLoaded(next, { entities: [selectId(data)], scope: scope ?? null }),
      next => adapter.optimisticUpdateRemove(next, correlationId),
      next => adapter.updateOne(next, data),
    ),
  updateOneOptimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    {
      data,
      correlationId,
      error,
      scope,
    }: EntityUpdateOperationPayload<Entity> & { error: HttpErrorResponse; scope?: string },
  ): EntityState<Entity, MetaOptions> => {
    const transaction = state.optimisticUpdate[correlationId];

    const rollbackUpdate = <EntityPartial<Entity>>(
      Object.fromEntries(
        Object.keys(transaction.update).map(updatedKey => [
          updatedKey,
          transaction.before[<keyof Entity>updatedKey],
        ]),
      )
    );

    return pipeState(
      state,
      next => adapter.optimisticUpdateRemove(next, correlationId),
      next =>
        adapter.setError(next, error, {
          entities: [selectId(transaction.before)],
          scope: scope ?? null,
        }),
      next => adapter.updateOne(next, rollbackUpdate),
    );
  },

  updateOnePessimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: { data: PartialEntity<IdSelector, Entity>; scope?: string },
  ): EntityState<Entity, MetaOptions> =>
    adapter.markAsPending(state, {
      entities: [selectId(payload.data)],
      scope: payload?.scope ?? null,
    }),
  updateOnePessimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data, scope }: ActionScopedPayload<Entity>,
  ): EntityState<Entity, MetaOptions> =>
    pipeState(
      state,
      next => adapter.markAsLoaded(next, { entities: [selectId(data)], scope: scope ?? null }),
      next => adapter.updateOne(next, data),
    ),
  updateOnePessimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    {
      data,
      error,
      scope,
    }: ActionScopedPayload<PartialEntity<IdSelector, Entity>> & { error: HttpErrorResponse },
  ): EntityState<Entity, MetaOptions> =>
    adapter.setError(state, error, { entities: [selectId(data)], scope: scope ?? null }),

  removeOne: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationSyncPayload<ID> & ActionScopedPayload<ID>,
  ): EntityState<Entity, MetaOptions> => adapter.removeOne(state, payload.data),

  // @TODO scope meta status to be updated with removeOneOptimisticSuccess
  removeOneOptimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: { data: ID },
  ): EntityState<Entity, MetaOptions> => {
    const before = state.entityMap[payload.data]!;

    return pipeState(
      state,
      next => adapter.markAsPending(next, { entities: [payload.data], scope: null }),
      next => adapter.optimisticDeleteSet(next, before),
      next => adapter.removeOne(next, payload.data),
    );
  },
  // @TODO remove one optimistic doesnt update scope meta status
  removeOneOptimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data }: { data: Entity },
  ): EntityState<Entity, MetaOptions> =>
    pipeState(state, next => adapter.optimisticDeleteRemove(next, data.id)),
  // @TODO scope meta status to be updated with removeOneOptimisticSuccess
  removeOneOptimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    { data: id, error }: { data: ID } & { error: HttpErrorResponse },
  ): EntityState<Entity, MetaOptions> => {
    const entity = state.optimisticDelete[id];

    return pipeState(
      state,
      next => adapter.optimisticDeleteRemove(next, id),
      next => adapter.setError(next, error!, { entities: [id], scope: null }),
      next => adapter.addOne(next, entity),
    );
  },

  // @TODO custom scope to be updated with removeOnePessimisticSuccess
  removeOnePessimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: { data: ReturnType<IdSelector> },
  ): EntityState<Entity, MetaOptions> =>
    adapter.markAsPending(state, { entities: [payload.data], scope: null }),
  // @TODO remove one pessimistic success doesnt update scope meta status
  removeOnePessimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data }: { data: Entity },
  ): EntityState<Entity, MetaOptions> => adapter.removeOne(state, data.id),
  // @TODO custom scope to be updated with removeOnePessimisticSuccess
  removeOnePessimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    { data: id, error }: { data: ID } & { error: HttpErrorResponse },
  ): EntityState<Entity, MetaOptions> =>
    adapter.setError(state, error!, { entities: [id], scope: null }),
});
